import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { BehaviorSubject, combineLatest, map, Observable, of, startWith, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { debounceTime, distinctUntilChanged, skip, take } from 'rxjs/operators';

import { ButtonTheme, IconPosition, InputType, PopoverOptions, PopoverService } from '@fcom/ui-components';
import { DestinationSearchFeatureState, DestinationSearchFilterStateMeta } from '@fcom/destination-search/interfaces';
import { FINNISH_SITES } from '@fcom/core';
import { ElementActions, ElementTypes, GaContext, MediaQueryService } from '@fcom/common';
import { DestinationSearchBlockOffer } from '@fcom/cms';
import { finShare } from '@fcom/rx';
import { GtmService } from '@fcom/common/gtm';
import { LanguageService } from '@fcom/ui-translate';

import {
  setMaxPrice,
  setShowDirectFlightsOnly,
  setRoundTripPrices,
} from '../../store/actions/destination-search-filter.actions';
import { destinationsBeforeFilteringState, filterState, selectedDestinationsState } from '../../store';
import { filterDestinations } from '../../utils/filters';

const DESTINATION_FILTER_POPOVER_CONTEXT = 'destination-search-filters';

const DEFAULT_POPOVER_OPTIONS: PopoverOptions = {
  context: DESTINATION_FILTER_POPOVER_CONTEXT,
  zIndex: 20,
};

export enum Stops {
  ALL_FLIGHTS = 'allFlights',
  DIRECT_FLIGHTS = 'directFlightsOnly',
}

@Component({
  selector: 'fin-destination-search-filters',
  templateUrl: 'destination-search-filters.component.html',
  styleUrls: ['destination-search-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DestinationSearchFiltersComponent implements OnInit, OnDestroy {
  readonly InputType = InputType;
  readonly IconPosition = IconPosition;
  readonly ButtonTheme = ButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly String = String;
  readonly Stops = Stops;

  @Input()
  sticky$: Observable<boolean>;

  @Input()
  searchBarVisible$: Observable<boolean>;

  @Input()
  meta: DestinationSearchFilterStateMeta;

  @Input()
  clearFilters$: Observable<void>;

  @Output()
  mobileFiltersOpened = new EventEmitter<boolean>();

  activeFilters$: Observable<number>;
  priceInputForm: FormGroup<{ priceInput: FormControl<string>; stops: FormControl<Stops> }>;
  roundTripOnly$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  numberOfFilteredDestinations$: Observable<number>;
  isMobile$: Observable<boolean>;
  priceInputValue$: Observable<number | null> = of(null);
  stopsInputvalue$: Observable<Stops> = of(Stops.ALL_FLIGHTS);

  priceButtonLabelKey$: Observable<string>;
  priceFilteringActive$: Observable<boolean>;

  filteredDestinations$: Observable<DestinationSearchBlockOffer[]>;
  hasShortHaulFlights$: Observable<boolean>;

  selectedDestinations$: Observable<string[]>;

  popoverOptions$: Observable<PopoverOptions> = of(DEFAULT_POPOVER_OPTIONS);

  showStopFilters: boolean;

  isModalOpen: boolean;

  subscription = new Subscription();

  constructor(
    private store$: Store<DestinationSearchFeatureState>,
    private popoverService: PopoverService,
    private mediaQueryService: MediaQueryService,
    private gtmService: GtmService,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');

    this.subscription.add(
      this.store$
        .pipe(filterState(this.meta.id), take(1))
        .subscribe(({ maxPrice, roundTripPrices, showDirectFlightsOnly }) => {
          this.priceInputForm = new FormGroup({
            priceInput: new FormControl(maxPrice ? String(maxPrice) : null),
            stops: new FormControl(showDirectFlightsOnly ? Stops.DIRECT_FLIGHTS : Stops.ALL_FLIGHTS),
          });
          this.roundTripOnly$.next(roundTripPrices);
        })
    );

    this.stopsInputvalue$ = this.priceInputForm.controls.stops.valueChanges.pipe(
      startWith(this.priceInputForm.controls.stops.value)
    );

    this.priceInputValue$ = this.priceInputForm.controls.priceInput.valueChanges.pipe(
      startWith(this.priceInputForm.controls.priceInput.value),
      map((value) => (value ? parseInt(value, 10) : null))
    );

    this.priceFilteringActive$ = combineLatest([this.priceInputValue$, this.roundTripOnly$]).pipe(
      map(([maxPrice, roundTripOnly]) => maxPrice !== null || roundTripOnly)
    );

    this.priceButtonLabelKey$ = combineLatest([this.roundTripOnly$, this.priceInputValue$]).pipe(
      map(([roundTripOnly, maxPrice]) => {
        if (roundTripOnly && maxPrice !== null) {
          return 'destinationSearch.filters.priceFilterLabelRoundTripAndMaxPrice';
        }
        if (roundTripOnly) {
          return 'destinationSearch.filters.priceFilterRoundTrip';
        }
        if (maxPrice !== null) {
          return 'destinationSearch.filters.priceFilterMaxPrice';
        }
        return 'destinationSearch.filters.priceLabel';
      })
    );

    this.popoverOptions$ = this.sticky$.pipe(
      map((sticky) => ({
        ...DEFAULT_POPOVER_OPTIONS,
        useFixedPosition: sticky,
      }))
    );

    this.showStopFilters = FINNISH_SITES.includes(this.languageService.langValue);

    const destinationsBeforeFiltering$ = this.store$.pipe(
      destinationsBeforeFilteringState(this.meta.id),
      distinctUntilChanged(
        (prev, next) => next.length === prev.length && next.every((d, i) => d.locationCode === prev[i].locationCode)
      ),
      finShare()
    );

    this.filteredDestinations$ = combineLatest([
      destinationsBeforeFiltering$,
      this.priceInputValue$,
      this.stopsInputvalue$,
      this.roundTripOnly$,
    ]).pipe(
      map(([destinationsBeforeFiltering, maxPrice, stops, roundTripOnly]) =>
        filterDestinations(destinationsBeforeFiltering, roundTripOnly, maxPrice, stops === Stops.DIRECT_FLIGHTS)
      ),
      finShare()
    );

    this.numberOfFilteredDestinations$ = this.filteredDestinations$.pipe(
      map((destinations) => destinations.length ?? 0)
    );

    this.hasShortHaulFlights$ = destinationsBeforeFiltering$.pipe(
      map((destinations) => destinations.some((d) => d.isShortHaul))
    );

    this.activeFilters$ = combineLatest([this.priceInputValue$, this.roundTripOnly$, this.stopsInputvalue$]).pipe(
      map(
        ([maxPrice, roundTripOnly, stops]) =>
          [maxPrice !== null, roundTripOnly, stops === Stops.DIRECT_FLIGHTS].filter(Boolean).length
      )
    );

    this.selectedDestinations$ = this.store$.pipe(
      selectedDestinationsState(this.meta.id),
      map((destinations) => destinations.map((destination) => destination.title))
    );

    this.subscription.add(
      combineLatest([this.roundTripOnly$, this.hasShortHaulFlights$]).subscribe(
        ([roundTripOnly, hasShortHaulFlights]) => {
          this.store$.dispatch(
            setRoundTripPrices({ meta: this.meta, roundTripPrices: roundTripOnly || !hasShortHaulFlights })
          );
        }
      )
    );

    this.subscription.add(
      this.stopsInputvalue$.subscribe((stops) => {
        this.store$.dispatch(
          setShowDirectFlightsOnly({
            showDirectFlightsOnly: stops === Stops.DIRECT_FLIGHTS,
            meta: this.meta,
          })
        );
      })
    );

    this.subscription.add(
      this.priceInputValue$.pipe(debounceTime(500)).subscribe((value) => {
        this.store$.dispatch(setMaxPrice({ maxPrice: value, meta: this.meta }));
      })
    );

    this.subscription.add(
      this.isMobile$.subscribe((isMobile) => {
        if (!isMobile) {
          this.popoverService.closeByContext(DESTINATION_FILTER_POPOVER_CONTEXT);
        }
      })
    );

    this.subscription.add(
      this.sticky$.subscribe(() => {
        this.popoverService.closeByContext(DESTINATION_FILTER_POPOVER_CONTEXT);
      })
    );

    this.subscription.add(
      this.searchBarVisible$.subscribe((isVisible) => {
        if (!isVisible) {
          this.popoverService.closeByContext(DESTINATION_FILTER_POPOVER_CONTEXT);
        }
      })
    );

    this.subscription.add(
      this.clearFilters$.subscribe(() => {
        this.resetAllFilters();
      })
    );

    this.subscription.add(
      this.priceInputForm.controls.stops.valueChanges.pipe(debounceTime(1000)).subscribe((value) => {
        if (value === Stops.DIRECT_FLIGHTS) {
          this.gtmService.trackElement(
            'custom-filter-select-direct-flights',
            GaContext.DESTINATION_MAIN_PAGE,
            ElementTypes.RADIO,
            'select-filter-settings',
            ElementActions.CLICK
          );
        }
      })
    );

    this.subscription.add(
      this.priceInputForm.controls.priceInput.valueChanges.pipe(debounceTime(1000)).subscribe((value) => {
        const parsedValue = parseInt(value, 10);
        if (parsedValue > 0) {
          this.gtmService.trackElement(
            'custom-filter-price-limit',
            GaContext.DESTINATION_MAIN_PAGE,
            ElementTypes.FORM,
            value,
            ElementActions.SUBMIT
          );
        }
      })
    );

    this.subscription.add(
      this.roundTripOnly$.pipe(skip(1), debounceTime(1000)).subscribe((value) => {
        if (value) {
          this.gtmService.trackElement(
            'custom-filter-select-price-round-trip',
            GaContext.DESTINATION_MAIN_PAGE,
            ElementTypes.CHECKBOX,
            'select-filter-settings',
            ElementActions.CLICK
          );
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  showModal(): void {
    this.isModalOpen = true;
    this.mobileFiltersOpened.emit(true);
    this.gtmService.trackElement(
      'custom-filters-open-all',
      GaContext.DESTINATION_MAIN_PAGE,
      ElementTypes.BUTTON,
      'open-filter-settings',
      ElementActions.OPEN
    );
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.mobileFiltersOpened.emit(false);
  }

  resetPriceFilters(): void {
    this.priceInputForm.controls.priceInput.setValue(null);
    this.roundTripOnly$.next(false);

    this.store$.dispatch(setMaxPrice({ maxPrice: null, meta: this.meta }));
  }

  resetStops(): void {
    this.priceInputForm.controls.stops.setValue(Stops.ALL_FLIGHTS);
  }

  resetAllFilters(): void {
    this.resetPriceFilters();
    this.resetStops();
  }

  closeFilters(): void {
    this.popoverService.closeByContext(DESTINATION_FILTER_POPOVER_CONTEXT);
  }

  priceFilterOpened(): void {
    this.gtmService.trackElement(
      'custom-filter-open-price',
      GaContext.DESTINATION_MAIN_PAGE,
      ElementTypes.BUTTON,
      'open-filter-settings',
      ElementActions.OPEN
    );
  }

  stopsFilterOpened(): void {
    this.gtmService.trackElement(
      'custom-filter-open-direct-flights',
      GaContext.DESTINATION_MAIN_PAGE,
      ElementTypes.BUTTON,
      'open-filter-settings',
      ElementActions.OPEN
    );
  }
}
